// src/pages/admin/AdminShowcasePanel.tsx
// Route: /admin/reviews/showcases (tutors, staff and admins)
//
// The online showcase panel. Each panel member watches a showcase video and
// scores it 1–5 on that week's criteria (the ones the lesson told the child
// about), with a comment. Other members' scores stay hidden until you have
// given yours. An admin then finalises it for the family (feedback, an
// optional award, the certificate) and approves the gallery premiere when
// the parent has agreed.

import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { selectAdminRole } from "../../app/features/adminAuthSlice";
import { errorText } from "../../app/api/learningApi";
import {
  AWARD_IDEAS,
  SCORE_LABELS,
  useFinaliseShowcaseMutation,
  useGetPanelEntriesQuery,
  useGetPanelEntryQuery,
  useScoreShowcaseMutation,
  useSetGalleryApprovalMutation,
} from "../../app/api/showcaseApi";
import type { PanelEntry, PanelView } from "../../app/api/showcaseApi";
import MediaPlayer from "../../components/MediaPlayer";
import { Button, Card, EmptyState, PageHeader, Skeleton, trackConfig } from "./AdminShared";

import { Star, Trophy } from "lucide-react";
const VIEWS: { key: PanelView; label: string }[] = [
  { key: "to_score", label: "To score" },
  { key: "to_finalise", label: "To finalise" },
  { key: "finalised", label: "Finalised" },
  { key: "gallery", label: "Gallery" },
];

const TRACKS = [
  { value: "", label: "All ages" },
  { value: "foundation_builders", label: "6–9" },
  { value: "explorers", label: "9–12" },
  { value: "innovators", label: "13–18" },
];

const fmt = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleString("en-NG", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" }) : "—";

/** "2026-10-09T18:00" in local time, for a datetime-local input. */
const toLocalInput = (d: Date) => {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

const ADMIN_ROLES = ["admin", "staff", "super_admin"];

/* ─── Scoring form ───────────────────────────────────────────────── */

function ScoreForm({ entry }: { entry: PanelEntry }) {
  const [score, { isLoading }] = useScoreShowcaseMutation();
  const [scores, setScores] = useState<(number | null)[]>(
    entry.myScore?.scores ?? entry.criteria.map(() => null),
  );
  const [comment, setComment] = useState(entry.myScore?.comment ?? "");
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);
  const complete = scores.every((v) => v !== null) && comment.trim().length >= 10;

  const save = async () => {
    setNote(null);
    try {
      await score({ id: entry.id, scores: scores as number[], comment: comment.trim() }).unwrap();
      setNote({ ok: true, text: "Score saved" });
    } catch (e) {
      setNote({ ok: false, text: errorText(e, "Could not save the score") });
    }
  };

  return (
    <div className="space-y-4">
      <p className="text-xs font-bold uppercase tracking-wider text-[#8189a0]">
        {entry.myScore ? "Your score (you can change it until it's finalised)" : "Your score"}
      </p>
      {entry.criteria.map((c, i) => (
        <div key={c}>
          <p className="text-sm font-semibold text-[#16213e]">{c}</p>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {[1, 2, 3, 4, 5].map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => setScores((s) => s.map((x, j) => (j === i ? v : x)))}
                className={`rounded-xl border px-2.5 py-1.5 text-xs font-semibold transition ${
                  scores[i] === v
                    ? "border-[#1b2d5e] bg-[#1b2d5e] text-white"
                    : "border-[#dde2ec] text-[#3b4560] hover:bg-[#f1f3f8]"
                }`}
                title={SCORE_LABELS[v]}
              >
                {v} · {SCORE_LABELS[v]}
              </button>
            ))}
          </div>
        </div>
      ))}
      <label className="block text-sm font-semibold text-[#16213e]">
        Comment for the panel
        <textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          rows={3}
          maxLength={2000}
          placeholder="What shone, and the one thing to work on next."
          className="mt-1.5 w-full rounded-xl border border-[#dde2ec] px-3 py-2 text-sm font-normal outline-none focus:border-[#1b2d5e]"
        />
      </label>
      <div className="flex items-center gap-3">
        <Button onClick={save} disabled={!complete || isLoading}>
          {isLoading ? "Saving…" : entry.myScore ? "Update score" : "Save score"}
        </Button>
        {note && <span className={`text-xs font-semibold ${note.ok ? "text-[#2f7a4f]" : "text-[#b94a3a]"}`}>{note.text}</span>}
      </div>
    </div>
  );
}

/* ─── Everyone's scores (after you've scored) ───────────────────── */

function PanelScores({ entry }: { entry: PanelEntry }) {
  if (!entry.panel) {
    return (
      <p className="rounded-xl bg-[#f1f3f8] px-3 py-2 text-xs text-[#5f6780]">
        {entry.panelCount === 0
          ? "Nobody has scored this yet."
          : `${entry.panelCount} panel member${entry.panelCount === 1 ? " has" : "s have"} scored this. Their scores show once you have given yours.`}
      </p>
    );
  }
  return (
    <div className="space-y-3">
      <p className="text-xs font-bold uppercase tracking-wider text-[#8189a0]">
        Panel · average {entry.average?.toFixed(1) ?? "—"}
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-[#8189a0]">
              <th className="py-1 pr-3 font-semibold">Criterion</th>
              {entry.panel.map((p, i) => (
                <th key={i} className="py-1 pr-3 font-semibold">{p.judgeName ?? `Member ${i + 1}`}</th>
              ))}
              <th className="py-1 font-semibold">Avg</th>
            </tr>
          </thead>
          <tbody>
            {entry.criteria.map((c, i) => (
              <tr key={c} className="border-t border-[#ebeff7]">
                <td className="py-1.5 pr-3 text-[#3b4560]">{c}</td>
                {entry.panel!.map((p, j) => (
                  <td key={j} className="py-1.5 pr-3 font-semibold text-[#16213e]">{p.scores[i]}</td>
                ))}
                <td className="py-1.5 font-bold text-[#1b2d5e]">{entry.criterionAverages?.[i]?.toFixed(1) ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {entry.panel.map((p, i) =>
        p.comment ? (
          <p key={i} className="text-xs leading-5 text-[#3b4560]">
            <b>{p.judgeName ?? `Member ${i + 1}`}:</b> {p.comment}
          </p>
        ) : null,
      )}
    </div>
  );
}

/* ─── Finalise (admins) ─────────────────────────────────────────── */

function FinaliseForm({ entry }: { entry: PanelEntry }) {
  const [finalise, { isLoading }] = useFinaliseShowcaseMutation();
  const [feedback, setFeedback] = useState("");
  const [award, setAward] = useState("");
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);
  const ideas = AWARD_IDEAS[entry.track] ?? [];
  const name = entry.child?.firstName ?? "the child";

  const submit = async () => {
    if (!window.confirm(`Send ${name}'s results to the family? Scores can't change after this.`)) return;
    setNote(null);
    try {
      const res = await finalise({ id: entry.id, feedback: feedback.trim(), award: award.trim() || undefined }).unwrap();
      setNote({ ok: true, text: res.message ?? "Finalised" });
    } catch (e) {
      setNote({ ok: false, text: errorText(e, "Could not finalise") });
    }
  };

  return (
    <div className="space-y-3 rounded-2xl border border-[#e5e8ef] bg-[#f9fafc] p-4">
      <p className="text-xs font-bold uppercase tracking-wider text-[#8189a0]">Finalise for the family</p>
      <label className="block text-sm font-semibold text-[#16213e]">
        Feedback to {name} and their family
        <textarea
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
          rows={5}
          maxLength={4000}
          placeholder={`Speak to ${name}: what shone in the showcase, and one thing to work on next term.`}
          className="mt-1.5 w-full rounded-xl border border-[#dde2ec] bg-white px-3 py-2 text-sm font-normal outline-none focus:border-[#1b2d5e]"
        />
      </label>
      <label className="block text-sm font-semibold text-[#16213e]">
        Award <span className="font-normal text-[#8189a0]">(optional)</span>
        <input
          value={award}
          onChange={(e) => setAward(e.target.value)}
          maxLength={80}
          list={`awards-${entry.id}`}
          placeholder="e.g. Clearest Customer"
          className="mt-1.5 w-full rounded-xl border border-[#dde2ec] bg-white px-3 py-2 text-sm font-normal outline-none focus:border-[#1b2d5e]"
        />
        <datalist id={`awards-${entry.id}`}>
          {ideas.map((a) => (
            <option key={a} value={a} />
          ))}
        </datalist>
      </label>
      <div className="flex items-center gap-3">
        <Button onClick={submit} disabled={isLoading || feedback.trim().length < 20}>
          {isLoading ? "Sending…" : "Finalise and email the family"}
        </Button>
        {note && <span className={`text-xs font-semibold ${note.ok ? "text-[#2f7a4f]" : "text-[#b94a3a]"}`}>{note.text}</span>}
      </div>
    </div>
  );
}

/* ─── Gallery approval (admins) ─────────────────────────────────── */

function GalleryApproval({ entry }: { entry: PanelEntry }) {
  const [setApproval, { isLoading }] = useSetGalleryApprovalMutation();
  const [when, setWhen] = useState(toLocalInput(new Date()));
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);
  const g = entry.gallery;

  const run = async (approve: boolean) => {
    setNote(null);
    try {
      const res = await setApproval({
        id: entry.id,
        approve,
        premiereAt: approve ? new Date(when).toISOString() : undefined,
      }).unwrap();
      setNote({ ok: true, text: res.message ?? "Saved" });
    } catch (e) {
      setNote({ ok: false, text: errorText(e, "Could not save") });
    }
  };

  return (
    <div className="space-y-3 rounded-2xl border border-[#e5e8ef] bg-[#f9fafc] p-4">
      <p className="text-xs font-bold uppercase tracking-wider text-[#8189a0]">Gallery</p>
      {!g.consent ? (
        <p className="text-sm text-[#5f6780]">The parent has not asked for this showcase to go in the gallery.</p>
      ) : g.approvedAt ? (
        <>
          <p className="text-sm text-[#3b4560]">
            “{entry.projectTitle}” {g.live ? "is in the gallery since" : "premieres"} {fmt(g.premiereAt)}.
          </p>
          <Button variant="secondary" size="sm" onClick={() => run(false)} disabled={isLoading}>
            Take it out of the gallery
          </Button>
        </>
      ) : (
        <>
          <p className="text-sm text-[#3b4560]">
            The parent agreed to share “{entry.projectTitle}” with academy families. Watch it once more for anything
            personal (surname, school, address) before approving.
          </p>
          <label className="block text-xs font-semibold text-[#3b4560]">
            Premiere
            <input
              type="datetime-local"
              value={when}
              onChange={(e) => setWhen(e.target.value)}
              className="mt-1 block rounded-xl border border-[#dde2ec] bg-white px-3 py-2 text-sm outline-none focus:border-[#1b2d5e]"
            />
          </label>
          <Button size="sm" onClick={() => run(true)} disabled={isLoading || !when}>
            Approve for the gallery
          </Button>
        </>
      )}
      {note && <p className={`text-xs font-semibold ${note.ok ? "text-[#2f7a4f]" : "text-[#b94a3a]"}`}>{note.text}</p>}
    </div>
  );
}

/* ─── Detail ─────────────────────────────────────────────────────── */

function EntryDetail({ id, isAdmin }: { id: string; isAdmin: boolean }) {
  const { data, isLoading, error } = useGetPanelEntryQuery(id);
  const e = data?.data;
  if (isLoading) return <Skeleton className="h-96" />;
  if (error || !e) return <p className="text-sm text-[#b94a3a]">{errorText(error, "Could not load the showcase")}</p>;
  const t = trackConfig(e.track);

  return (
    <Card className="space-y-5 p-5">
      <div>
        <p className="text-[12.5px] font-bold uppercase tracking-wider text-[#8189a0]">
          {t.label} · {e.showcaseName} · attempt {e.attemptNumber}
        </p>
        <p className="text-lg font-bold text-[#16213e]">
          {e.child ? `${e.child.firstName} ${e.child.lastInitial}.` : "A learner"}
          {e.child?.age ? <span className="font-normal text-[#8189a0]"> · age {e.child.age}</span> : null}
        </p>
        <p className="text-xs text-[#8189a0]">Handed in {fmt(e.submittedAt)}</p>
      </div>

      {e.videoUrl ? <MediaPlayer src={e.videoUrl} /> : <p className="text-sm text-[#b94a3a]">The video is missing.</p>}

      {(e.reflection.built || e.reflection.helps || e.reflection.learned) && (
        <div className="space-y-1 rounded-2xl bg-[#f1f3f8] p-4 text-sm text-[#3b4560]">
          {e.reflection.built && <p><b>What I built:</b> {e.reflection.built}</p>}
          {e.reflection.helps && <p><b>Who it helps:</b> {e.reflection.helps}</p>}
          {e.reflection.learned && <p><b>What I learned:</b> {e.reflection.learned}</p>}
        </div>
      )}

      {e.status === "awaiting_panel" && <ScoreForm key={e.id + (e.myScore?.at ?? "")} entry={e} />}
      <PanelScores entry={e} />

      {e.status === "finalised" && e.result && (
        <div className="space-y-1 rounded-2xl border border-[#d6efe2] bg-[#f3fbf7] p-4 text-sm text-[#16213e]">
          <p className="text-xs font-bold uppercase tracking-wider text-[#2a7a56]">Sent to the family {fmt(e.result.finalisedAt)}</p>
          {e.result.award && <p><Trophy className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />{" "}{e.result.award}</p>}
          <p className="whitespace-pre-line">{e.result.feedback}</p>
        </div>
      )}

      {isAdmin && e.canFinalise && <FinaliseForm entry={e} />}
      {isAdmin && e.status === "finalised" && <GalleryApproval entry={e} />}
    </Card>
  );
}

/* ─── Page ───────────────────────────────────────────────────────── */

export default function AdminShowcasePanel() {
  const role = useSelector(selectAdminRole);
  const isAdmin = ADMIN_ROLES.includes(role ?? "");
  const [view, setView] = useState<PanelView>("to_score");
  const [track, setTrack] = useState("");
  const [selected, setSelected] = useState<string | null>(null);
  const { data, isLoading } = useGetPanelEntriesQuery({ view, track }, { pollingInterval: 60_000 });
  const items = useMemo(() => data?.data ?? [], [data]);
  const counts = data?.counts;

  useEffect(() => {
    if (selected && !items.some((i) => i.id === selected) && items.length) setSelected(items[0].id);
    if (!selected && items.length) setSelected(items[0].id);
  }, [items, selected]);

  const views = isAdmin ? VIEWS : VIEWS.filter((v) => v.key === "to_score" || v.key === "finalised");

  return (
    <>
      <PageHeader
        title="Showcase panel"
        subtitle="Watch each showcase video and score it on the criteria its lesson told the child about. Every family gets feedback and a certificate."
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        {views.map((v) => (
          <button
            key={v.key}
            type="button"
            onClick={() => {
              setView(v.key);
              setSelected(null);
            }}
            className={`rounded-full px-3.5 py-1.5 text-xs font-semibold ${
              view === v.key ? "bg-[#1b2d5e] text-white" : "border border-[#dde2ec] text-[#3b4560] hover:bg-[#f1f3f8]"
            }`}
          >
            {v.label}
            {counts ? ` · ${counts[v.key]}` : ""}
          </button>
        ))}
        <span className="mx-1 h-5 w-px bg-[#dde2ec]" />
        {TRACKS.map((t) => (
          <button
            key={t.value}
            type="button"
            onClick={() => {
              setTrack(t.value);
              setSelected(null);
            }}
            className={`rounded-full px-3 py-1 text-xs font-semibold ${
              track === t.value ? "bg-[#16213e] text-white" : "text-[#5f6780] hover:bg-[#f1f3f8]"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <Skeleton className="h-64" />
      ) : items.length === 0 ? (
        <EmptyState
          icon={Star}
          title="Nothing here"
          subtitle={
            view === "to_score"
              ? "No showcase videos are waiting for your score."
              : "Showcase videos appear here when children hand them in on a showcase week."
          }
        />
      ) : (
        <div className="grid gap-5 lg:grid-cols-[320px_1fr]">
          <div className="space-y-2">
            {items.map((e) => (
              <button
                key={e.id}
                type="button"
                onClick={() => setSelected(e.id)}
                className={`w-full rounded-2xl border p-4 text-left transition ${
                  selected === e.id ? "border-[#1b2d5e] bg-[#f1f3f8]" : "border-[#e5e8ef] bg-white hover:bg-[#f9fafc]"
                }`}
              >
                <p className="text-sm font-bold text-[#16213e]">
                  {e.child ? `${e.child.firstName} ${e.child.lastInitial}.` : "A learner"}
                </p>
                <p className="text-[12.5px] text-[#8189a0]">
                  {trackConfig(e.track).label} · {e.showcaseName}
                </p>
                <p className="mt-1 text-[12.5px] text-[#5f6780]">
                  {e.panelCount} score{e.panelCount === 1 ? "" : "s"}
                  {e.myScore ? " · you scored" : ""}
                  {e.average != null ? ` · avg ${e.average.toFixed(1)}` : ""}
                  {e.gallery.consent && view === "gallery" ? (e.gallery.live ? " · live" : e.gallery.approvedAt ? " · scheduled" : " · waiting for approval") : ""}
                </p>
              </button>
            ))}
          </div>
          <div>{selected && <EntryDetail key={selected} id={selected} isAdmin={isAdmin} />}</div>
        </div>
      )}
    </>
  );
}
